import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { appraisalDetailApi } from '@/api/appraisalDetailApi';
import type {
  UpdateInterviewCandidateRequest,
  UpdateSkillAreaFeedbackRequest,
  UpdateNotesToTalentCommitteeRequest,
  UpdatePerformanceSummaryRequest,
  ForwardLookingPlanItem,
  SelfAssessmentItem,
  AddInterviewSummaryEntryRequest,
  AiImportRequest,
  AppraisalDetailResponse,
  InterviewCandidate,
  InterviewSummaryEntry,
} from '@/api/types/appraisal.types';
import { PERMISSIONS_QUERY_KEY } from './usePermissions';
import { DASHBOARD_QUERY_KEY } from './useDashboard';

const pendingByAppraisal = new Map<number, Promise<unknown>>();

export const appraisalDetailKeys = {
  all: ['appraisalDetail'] as const,
  detail: (id: string) => ['appraisalDetail', id] as const,
  skillAreaDetails: (appraisalId: string, skillAreaId: number) =>
    ['appraisalDetail', appraisalId, 'skillAreaDetails', skillAreaId] as const,
  eligibleProxies: (appraisalId: string, search: string) =>
    ['appraisalDetail', appraisalId, 'eligibleProxies', search] as const,
  interviewSummary: (id: number) => ['appraisalDetail', id, 'interviewSummary'] as const,
  staffSearch: (id: number, q: string) => ['appraisalDetail', id, 'staffSearch', q] as const,
  auditHistory: (id: string) => ['appraisalDetail', id, 'auditHistory'] as const,
  priorAppraisal: (id: string) => ['appraisals', id, 'prior'] as const,
  upwardFeedbackEmbed: (id: number) => ['appraisalDetail', id, 'upwardFeedbackEmbed'] as const,
};

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

const msUntilTokenRefresh = (tokenExpiry: string | undefined): number | false => {
  if (!tokenExpiry) return false;
  const expiresAt = new Date(tokenExpiry).getTime();
  if (Number.isNaN(expiresAt)) return false;
  return Math.max(expiresAt - Date.now() - TOKEN_REFRESH_MARGIN_MS, 1000);
};

export function useUpwardFeedbackEmbed(appraisalId: number) {
  return useQuery({
    queryKey: appraisalDetailKeys.upwardFeedbackEmbed(appraisalId),
    queryFn: () => appraisalDetailApi.getUpwardFeedbackEmbedConfig(appraisalId),
    // Power BI clamps the embed token's life to the AAD token's, so the refresh window
    // comes from the expiry the server reports rather than a fixed guess.
    staleTime: (query) => msUntilTokenRefresh(query.state.data?.tokenExpiry ?? undefined) || 0,
    refetchInterval: (query) => msUntilTokenRefresh(query.state.data?.tokenExpiry ?? undefined),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAppraisalDetail(id: string) {
  return useQuery({
    queryKey: appraisalDetailKeys.detail(id),
    queryFn: () => appraisalDetailApi.getAppraisalDetail(Number(id)),
    enabled: !!id,
  });
}

export function useUpdateInterviewCandidate() {
  const queryClient = useQueryClient();
  const pendingByAppraisal = useRef<Map<number, Promise<unknown>>>(new Map());

  return useMutation({
    mutationFn: async ({
      appraisalId,
      candidateId,
      data,
      rowVersion,
    }: {
      appraisalId: number;
      candidateId: number;
      data: UpdateInterviewCandidateRequest;
      rowVersion?: string;
    }) => {
      const previous = pendingByAppraisal.current.get(appraisalId);
      if (previous) {
        await previous.catch(() => {});
      }

      const cached = queryClient.getQueryData<AppraisalDetailResponse>(
        appraisalDetailKeys.detail(String(appraisalId))
      );
      const effectiveRowVersion = cached?.rowVersion ?? rowVersion;

      const promise = (async () => {
        await appraisalDetailApi.updateInterviewCandidate(
          appraisalId,
          candidateId,
          data,
          effectiveRowVersion
        );
        await queryClient.refetchQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
          exact: true,
        });
      })();

      pendingByAppraisal.current.set(appraisalId, promise);
      try {
        return await promise;
      } finally {
        if (pendingByAppraisal.current.get(appraisalId) === promise) {
          pendingByAppraisal.current.delete(appraisalId);
        }
      }
    },
    onMutate: ({ appraisalId, candidateId, data }) => {
      const detailKey = appraisalDetailKeys.detail(String(appraisalId));
      const summaryKey = appraisalDetailKeys.interviewSummary(appraisalId);

      const previousDetail = queryClient.getQueryData<AppraisalDetailResponse>(detailKey);
      const previousSummary = queryClient.getQueryData<InterviewSummaryEntry[]>(summaryKey);

      const checkboxChanges: Partial<InterviewCandidate> = {};
      if (data.appraiseSuggested !== undefined) {
        checkboxChanges.appraiseSuggested = data.appraiseSuggested;
      }
      if (data.planned !== undefined) {
        checkboxChanges.planned = data.planned;
      }
      if (data.completed !== undefined) {
        checkboxChanges.completed = data.completed;
      }

      const staffId =
        previousDetail?.projectGroups
          .flatMap((group) => group.interviewCandidates)
          .find((candidate) => candidate.id === candidateId)?.staffId ??
        previousSummary?.find((entry) => entry.candidateIds.includes(candidateId))?.staffId;

      if (previousDetail) {
        queryClient.setQueryData<AppraisalDetailResponse>(detailKey, {
          ...previousDetail,
          projectGroups: previousDetail.projectGroups.map((group) => ({
            ...group,
            interviewCandidates: group.interviewCandidates.map((candidate) => {
              // the server propagates checkboxes to every row for the same staff member,
              // keeps reason per-project, and ignores a null reason entirely
              const isTarget = candidate.id === candidateId;
              const isSameStaff = staffId !== undefined && candidate.staffId === staffId;
              if (!isTarget && !isSameStaff) {
                return candidate;
              }
              return {
                ...candidate,
                ...(isSameStaff ? checkboxChanges : {}),
                ...(isTarget && data.reason != null ? { reason: data.reason } : {}),
              };
            }),
          })),
        });
      }

      if (previousSummary) {
        queryClient.setQueryData<InterviewSummaryEntry[]>(
          summaryKey,
          previousSummary.map((entry) =>
            entry.candidateIds.includes(candidateId) ||
            (staffId !== undefined && entry.staffId === staffId)
              ? { ...entry, ...checkboxChanges }
              : entry
          )
        );
      }

      return { previousDetail, previousSummary };
    },
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(String(appraisalId)),
      });
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.interviewSummary(appraisalId),
      });
    },
    onError: (error, { appraisalId }, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          appraisalDetailKeys.detail(String(appraisalId)),
          context.previousDetail
        );
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(
          appraisalDetailKeys.interviewSummary(appraisalId),
          context.previousSummary
        );
      }

      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
        });
      }
    },
  });
}

export function useUpdateSkillAreaFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appraisalId,
      skillAreaId,
      data,
      rowVersion,
    }: {
      appraisalId: number;
      skillAreaId: number;
      data: UpdateSkillAreaFeedbackRequest;
      rowVersion?: string;
    }) => appraisalDetailApi.updateSkillAreaFeedback(appraisalId, skillAreaId, data, rowVersion),
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(String(appraisalId)),
      });
    },
    onError: (error, { appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
        });
      }
    },
  });
}

export function useSkillAreaDetails(appraisalId: string, skillAreaId: number, enabled: boolean) {
  return useQuery({
    queryKey: appraisalDetailKeys.skillAreaDetails(appraisalId, skillAreaId),
    queryFn: () => appraisalDetailApi.getSkillAreaDetails(Number(appraisalId), skillAreaId),
    enabled: !!appraisalId && enabled,
  });
}

export function useDelegateAppraisal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appraisalId, delegateStaffId, rowVersion }: { appraisalId: number; delegateStaffId: number; rowVersion?: string }) =>
      appraisalDetailApi.delegateAppraisal(appraisalId, delegateStaffId, rowVersion),
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(String(appraisalId)),
      });
    },
  });
}

export function useRevokeAppraisalDelegate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appraisalId, rowVersion }: { appraisalId: number; rowVersion?: string }) =>
      appraisalDetailApi.revokeAppraisalDelegate(appraisalId, rowVersion),
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(String(appraisalId)),
      });
    },
  });
}

export function useEligibleProxies(appraisalId: string, search: string) {
  return useQuery({
    queryKey: appraisalDetailKeys.eligibleProxies(appraisalId, search),
    queryFn: () => appraisalDetailApi.getEligibleProxies(Number(appraisalId), search),
    enabled: !!appraisalId,
  });
}

export function useUpdateForwardLookingPlan(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ items, rowVersion }: { items: ForwardLookingPlanItem[]; rowVersion?: string }) =>
      appraisalDetailApi.updateForwardLookingPlan(Number(appraisalId), items, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useUpdateNotesToTalentCommittee(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, rowVersion }: { data: UpdateNotesToTalentCommitteeRequest; rowVersion?: string }) => {
      const previous = pendingByAppraisal.get(Number(appraisalId));
      if (previous) {
        await previous.catch(() => {});
      }

      const cached = queryClient.getQueryData<AppraisalDetailResponse>(
        appraisalDetailKeys.detail(String(appraisalId))
      );
      const effectiveRowVersion = cached?.rowVersion ?? rowVersion;

      const promise = (async () => {
        await appraisalDetailApi.updateNotesToTalentCommittee(
          Number(appraisalId),
          data,
          effectiveRowVersion
        );
        await queryClient.refetchQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
          exact: true,
        });
      })();

      pendingByAppraisal.set(Number(appraisalId), promise);
      try {
        return await promise;
      } finally {
        if (pendingByAppraisal.get(Number(appraisalId)) === promise) {
          pendingByAppraisal.delete(Number(appraisalId));
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useUpdatePerformanceSummary(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, rowVersion }: { data: UpdatePerformanceSummaryRequest; rowVersion?: string }) =>
      appraisalDetailApi.updatePerformanceSummary(Number(appraisalId), data, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useInterviewSummary(appraisalId: number) {
  return useQuery({
    queryKey: appraisalDetailKeys.interviewSummary(appraisalId),
    queryFn: () => appraisalDetailApi.getInterviewSummary(appraisalId),
    enabled: !!appraisalId,
  });
}

export function useAddInterviewSummaryEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appraisalId, data }: { appraisalId: number; data: AddInterviewSummaryEntryRequest }) =>
      appraisalDetailApi.addInterviewSummaryEntry(appraisalId, data),
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.interviewSummary(appraisalId),
      });
    },
  });
}

export function useDeleteInterviewSummaryEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appraisalId, candidateId }: { appraisalId: number; candidateId: number }) =>
      appraisalDetailApi.deleteInterviewSummaryEntry(appraisalId, candidateId),
    onSuccess: (_, { appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.interviewSummary(appraisalId),
      });
    },
  });
}

export function useStaffSearch(appraisalId: number, query: string) {
  return useQuery({
    queryKey: appraisalDetailKeys.staffSearch(appraisalId, query),
    queryFn: () => appraisalDetailApi.searchInterviewStaff(appraisalId, query),
    enabled: !!appraisalId && query.length >= 2,
  });
}

export function useInterviewSummaryReport(appraisalId: number) {
  return useMutation({
    mutationFn: () => appraisalDetailApi.getInterviewSummaryReport(appraisalId),
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  });
}

export function useUpdateSelfAssessment(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ items, additionalContext, rowVersion }: { items: SelfAssessmentItem[]; additionalContext?: string; rowVersion?: string }) =>
      appraisalDetailApi.updateSelfAssessment(Number(appraisalId), items, additionalContext, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      // First edit transitions NotStarted → SelfAssessment, which flips canSubmitSelfAssessmentToCollator.
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      // Status chip on dashboard needs to refresh too.
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useSubmitAppraisal(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rowVersion }: { rowVersion?: string }) =>
      appraisalDetailApi.submitAppraisal(Number(appraisalId), rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useReturnToAppraisee(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ rowVersion }: { rowVersion?: string }) => {
      await pendingByAppraisal.get(Number(appraisalId))?.catch(() => {});
      const effectiveRowVersion =
        queryClient.getQueryData<AppraisalDetailResponse>(
          appraisalDetailKeys.detail(String(appraisalId))
        )?.rowVersion ?? rowVersion;
      return appraisalDetailApi.returnToAppraisee(Number(appraisalId), effectiveRowVersion);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useAppraisalAuditHistory(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: appraisalDetailKeys.auditHistory(id),
    queryFn: () => appraisalDetailApi.getAuditHistory(Number(id)),
    enabled: !!id && enabled,
  });
}

export function useExportForAi() {
  return useMutation({
    mutationFn: ({ appraisalId }: { appraisalId: number; firstName: string; lastName: string }) =>
      appraisalDetailApi.exportForAi(appraisalId),
    onSuccess: (data, { firstName, lastName }) => {
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `appraisal-ai-export-${firstName}-${lastName}-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  });
}

export function useImportFromAi() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appraisalId, data }: { appraisalId: number; data: AiImportRequest }) =>
      appraisalDetailApi.importFromAi(appraisalId, data),
    onSuccess: (responseData: AppraisalDetailResponse, { appraisalId }) => {
      queryClient.setQueryData(
        appraisalDetailKeys.detail(String(appraisalId)),
        responseData
      );
    },
    onError: (error, { appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
        });
      }
    },
  });
}

export function useConfirmDelivery(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rowVersion }: { rowVersion?: string }) =>
      appraisalDetailApi.confirmDelivery(Number(appraisalId), rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useSubmitToTalentCommittee(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ rowVersion }: { rowVersion?: string }) => {
      await pendingByAppraisal.get(Number(appraisalId))?.catch(() => {});
      const effectiveRowVersion =
        queryClient.getQueryData<AppraisalDetailResponse>(
          appraisalDetailKeys.detail(String(appraisalId))
        )?.rowVersion ?? rowVersion;
      return appraisalDetailApi.submitToTalentCommittee(Number(appraisalId), effectiveRowVersion);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function useDeliverToAppraisee(appraisalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      rowVersion,
      deliveryDate,
      deliveryTime,
    }: {
      rowVersion?: string;
      deliveryDate?: string;
      deliveryTime?: string;
    }) =>
      appraisalDetailApi.deliverToAppraisee(
        Number(appraisalId),
        rowVersion,
        deliveryDate,
        deliveryTime,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: PERMISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(appraisalId),
        });
      }
    },
  });
}

export function usePriorAppraisal(appraisalId: string) {
  return useQuery({
    queryKey: appraisalDetailKeys.priorAppraisal(appraisalId),
    queryFn: () => appraisalDetailApi.getPriorAppraisal(Number(appraisalId)),
    enabled: !!appraisalId,
  });
}
